import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import * as THREE from "three";

export function SceneCameraBinder({
  cameraObject,
  enabled,
}: {
  cameraObject: THREE.Camera | null;
  enabled: boolean;
}) {
  const { camera, set, size } = useThree();
  const previousCameraRef = useRef<THREE.Camera | null>(null);

  useEffect(() => {
    if (!enabled || !cameraObject) return;

    // Save the original viewer camera only once — re-running this effect after
    // the swap must not overwrite it with the GLTF camera (which would make
    // cleanup restore the wrong camera).
    if (!previousCameraRef.current) {
      previousCameraRef.current = camera;
    }

    // Ensure the camera's world matrix is current before the renderer starts
    // using it — otherwise the first frames can show a stale transform.
    cameraObject.updateMatrixWorld(true);

    // Skip the swap if this camera is already active (avoids a no-op set call).
    if (camera !== cameraObject) {
      set({ camera: cameraObject as THREE.PerspectiveCamera });
    }

    return () => {
      if (previousCameraRef.current) {
        set({ camera: previousCameraRef.current as THREE.PerspectiveCamera });
        // Clear the ref so a future re-enable captures the correct camera again.
        previousCameraRef.current = null;
      }
    };
    // Intentionally omit `camera` from deps — including it causes a dependency
    // loop: set() changes `camera`, which re-fires this effect, which calls set()
    // again and overwrites previousCameraRef with the GLTF camera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, cameraObject, set]);

  useEffect(() => {
    if (!enabled || !cameraObject) return;
    // Use instanceof instead of the isPerspectiveCamera flag to get correct
    // TypeScript narrowing and handle OrthographicCamera safely.
    if (cameraObject instanceof THREE.PerspectiveCamera) {
      cameraObject.aspect = size.width / size.height;
      cameraObject.updateProjectionMatrix();
    }
  }, [enabled, cameraObject, size.width, size.height]);

  return null;
}
